import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  RefreshCw,
  Search,
  RotateCcw,
  Eye,
  ExternalLink,
  MessageSquare,
  Bot,
  Mail,
  CheckCircle2,
  XCircle,
  Clock,
  Send
} from "lucide-react";
import { format, subDays } from "date-fns";
import { useHistory } from "react-router-dom";

import { obterDisparosRobo } from "../../services/relatorioDisparosService";
import toastError from "../../errors/toastError";
import KpiCards from "./KpiCards";
import DisparoDetalhesModal from "./DisparoDetalhesModal";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh"
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2)
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5)
  },
  title: {
    fontWeight: 700,
    fontSize: "1.5rem",
    color: theme.palette.text.primary
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem"
  },
  filterPaper: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
  },
  tablePaper: {
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
  },
  tableHeaderCell: {
    fontWeight: 700,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontSize: "0.85rem"
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: 6
  },
  channelChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
    fontWeight: 500
  },
  syncText: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 6
  },
  spinning: {
    animation: "$spin 1s linear infinite"
  },
  "@keyframes spin": {
    "100%": {
      transform: "rotate(360deg)"
    }
  }
}));

const RelatorioDisparosRobo = () => {
  const classes = useStyles();
  const history = useHistory();

  // Estados de dados
  const [loading, setLoading] = useState(false);
  const [disparos, setDisparos] = useState([]);
  const [paginacao, setPaginacao] = useState({
    pagina_atual: 1,
    limite: 25,
    total_registros: 0,
    total_paginas: 1
  });
  const [lastSync, setLastSync] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [resumoFiltro, setResumoFiltro] = useState(null);

  // Modal
  const [selectedDisparo, setSelectedDisparo] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Função utilitária para formatar data e hora com segurança
  const formatarDataHora = (dataStr) => {
    if (!dataStr) return "—";
    if (typeof dataStr !== "string") return String(dataStr);
    // Se já vier no formato brasileiro (ex: 28/09/2026 15:44:12), retorna
    if (/^\d{2}\/\d{2}\/\d{4}/.test(dataStr)) {
      return dataStr;
    }
    try {
      const parsed = new Date(dataStr);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, "dd/MM/yyyy HH:mm:ss");
      }
    } catch (e) {}
    return dataStr;
  };

  // Filtros - Padrão: início do mês atual até hoje
  const [busca, setBusca] = useState("");
  const [canal, setCanal] = useState("todos");
  const [statusEnvio, setStatusEnvio] = useState("todos");
  const [tipoMensagem, setTipoMensagem] = useState("todos");
  const [dataInicio, setDataInicio] = useState(
    format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd")
  );
  const [dataFim, setDataFim] = useState(format(new Date(), "yyyy-MM-dd"));

  const [errorMessage, setErrorMessage] = useState(null);

  const carregarDisparos = useCallback(
    async (forcarAtualizacao = false, paginaDestino = paginacao.pagina_atual) => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const params = {
          pagina: paginaDestino,
          limite: paginacao.limite,
          data_inicio: dataInicio,
          data_fim: dataFim,
          canal: canal !== "todos" ? canal : undefined,
          status_envio: statusEnvio !== "todos" ? statusEnvio : undefined,
          tipo_mensagem: tipoMensagem !== "todos" ? tipoMensagem : undefined,
          busca: busca ? busca.trim() : undefined,
          forcar_atualizacao: forcarAtualizacao ? "true" : undefined
        };

        const res = await obterDisparosRobo(params);

        if (res && res.data) {
          setDisparos(res.data);
          if (res.paginacao) {
            setPaginacao(res.paginacao);
          }
          if (res.resumo_filtro) {
            setResumoFiltro(res.resumo_filtro);
          }
          if (res._lastSync) {
            setLastSync(new Date(res._lastSync));
          } else {
            setLastSync(new Date());
          }
          setIsCached(!!res._cached);
        }
      } catch (err) {
        const msg =
          err.response?.data?.details ||
          err.response?.data?.error ||
          err.message ||
          "Não foi possível obter os logs da API Timbre.";
        setErrorMessage(msg);
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [
      paginacao.pagina_atual,
      paginacao.limite,
      dataInicio,
      dataFim,
      canal,
      statusEnvio,
      tipoMensagem,
      busca
    ]
  );

  useEffect(() => {
    carregarDisparos(false, 1);
  }, []); // Executa na montagem

  const handleFiltrar = (e) => {
    if (e) e.preventDefault();
    setPaginacao((prev) => ({ ...prev, pagina_atual: 1 }));
    carregarDisparos(false, 1);
  };

  const handleLimparFiltros = () => {
    setBusca("");
    setCanal("todos");
    setStatusEnvio("todos");
    setTipoMensagem("todos");
    const dIni = format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd");
    const dFim = format(new Date(), "yyyy-MM-dd");
    setDataInicio(dIni);
    setDataFim(dFim);
    setPaginacao((prev) => ({ ...prev, pagina_atual: 1 }));

    // Dispara recarga
    setLoading(true);
    obterDisparosRobo({
      pagina: 1,
      limite: paginacao.limite,
      data_inicio: dIni,
      data_fim: dFim
    })
      .then((res) => {
        if (res?.data) {
          setDisparos(res.data);
          if (res.paginacao) setPaginacao(res.paginacao);
          if (res.resumo_filtro) setResumoFiltro(res.resumo_filtro);
          if (res._lastSync) setLastSync(new Date(res._lastSync));
          setIsCached(!!res._cached);
        }
      })
      .catch((err) => toastError(err))
      .finally(() => setLoading(false));
  };

  const handleChangePage = (event, newPage) => {
    const proximaPagina = newPage + 1;
    setPaginacao((prev) => ({ ...prev, pagina_atual: proximaPagina }));
    carregarDisparos(false, proximaPagina);
  };

  const handleChangeRowsPerPage = (event) => {
    const novoLimite = parseInt(event.target.value, 10);
    setPaginacao((prev) => ({ ...prev, limite: novoLimite, pagina_atual: 1 }));
    setLoading(true);
    obterDisparosRobo({
      pagina: 1,
      limite: novoLimite,
      data_inicio: dataInicio,
      data_fim: dataFim,
      canal: canal !== "todos" ? canal : undefined,
      status_envio: statusEnvio !== "todos" ? statusEnvio : undefined,
      tipo_mensagem: tipoMensagem !== "todos" ? tipoMensagem : undefined,
      busca: busca ? busca.trim() : undefined
    })
      .then((res) => {
        if (res?.data) {
          setDisparos(res.data);
          if (res.paginacao) setPaginacao(res.paginacao);
          if (res.resumo_filtro) setResumoFiltro(res.resumo_filtro);
        }
      })
      .catch((err) => toastError(err))
      .finally(() => setLoading(false));
  };

  const handleOpenDetails = (disparo) => {
    setSelectedDisparo(disparo);
    setModalOpen(true);
  };

  const renderStatusBadge = (statusEnvioStr, label) => {
    const rawLabel = label || statusEnvioStr || "";
    const formattedLabel = rawLabel
      ? rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1)
      : "—";
    const status = (statusEnvioStr || "").toLowerCase();

    let bg = "#e2e8f0";
    let color = "#475569";
    let icon = null;

    if (status.includes("sucesso")) {
      bg = "rgba(22, 163, 74, 0.15)";
      color = "#15803d";
      icon = <CheckCircle2 size={13} style={{ marginRight: 4 }} />;
    } else if (status.includes("erro") || status.includes("falha")) {
      bg = "rgba(220, 38, 38, 0.15)";
      color = "#b91c1c";
      icon = <XCircle size={13} style={{ marginRight: 4 }} />;
    } else if (status.includes("cancelado")) {
      bg = "rgba(234, 88, 12, 0.15)";
      color = "#c2410c";
      icon = <Clock size={13} style={{ marginRight: 4 }} />;
    } else {
      bg = "rgba(202, 138, 4, 0.15)";
      color = "#a16207";
      icon = <Clock size={13} style={{ marginRight: 4 }} />;
    }

    return (
      <Box
        component="span"
        style={{
          display: "inline-flex",
          alignItems: "center",
          backgroundColor: bg,
          color: color,
          padding: "3px 8px",
          borderRadius: 6,
          fontWeight: 600,
          fontSize: "0.75rem"
        }}
      >
        {icon}
        {formattedLabel}
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      {/* 1. Header com Título, Status de Sincronização e Botão Atualizar */}
      <Box className={classes.header}>
        <Box className={classes.titleWrapper}>
          <Box
            style={{
              backgroundColor: "#22c55e",
              color: "#fff",
              borderRadius: 8,
              padding: 8,
              display: "flex",
              alignItems: "center"
            }}
          >
            <Bot size={28} />
          </Box>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography className={classes.title}>Disparos Robô RPA</Typography>
              <Chip
                label="Timbre"
                size="small"
                style={{
                  backgroundColor: "#202C33",
                  color: "#22c55e",
                  fontWeight: 700,
                  fontSize: "0.75rem"
                }}
              />
            </Box>
            <Typography className={classes.subtitle}>
              Auditoria e acompanhamento em tempo real dos disparos GiSign x Ícarus x Meta
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center">
          {lastSync && (
            <Box textAlign="right" style={{ marginRight: 24 }}>
              <Typography className={classes.syncText}>
                <Clock size={14} />
                Sincronizado: {format(lastSync, "HH:mm:ss")}
                {isCached && " (Cache 15m)"}
              </Typography>
              <Typography variant="caption" style={{ color: "#94a3b8", fontSize: "0.72rem" }}>
                Auto-sync a cada 15 min no servidor
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={<RefreshCw size={16} className={loading ? classes.spinning : ""} />}
            onClick={() => carregarDisparos(true)}
            style={{ fontWeight: 600, padding: "8px 18px" }}
          >
            Atualizar Agora
          </Button>
        </Box>
      </Box>

      {/* 2. Cards de KPIs */}
      <KpiCards
        items={disparos}
        totalRegistros={paginacao.total_registros}
        resumo={resumoFiltro}
      />

      {/* 3. Barra de Filtros */}
      <Paper className={classes.filterPaper}>
        <form onSubmit={handleFiltrar}>
          <Grid container spacing={2} alignItems="center">
            {/* Linha 1: Filtros textuais e categóricos */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Buscar Colaborador / CPF / Matrícula"
                placeholder="Ex: Rodrigo ou 030766..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Canal</InputLabel>
                <Select
                  value={canal}
                  onChange={(e) => setCanal(e.target.value)}
                  label="Canal"
                >
                  <MenuItem value="todos">Todos os Canais</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp Oficial</MenuItem>
                  <MenuItem value="email">E-mail</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusEnvio}
                  onChange={(e) => setStatusEnvio(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="todos">Todos os Status</MenuItem>
                  <MenuItem value="sucesso">Sucesso</MenuItem>
                  <MenuItem value="erro">Erro / Falha</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                  <MenuItem value="enfileirado">Enfileirado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Etapa / Mensagem</InputLabel>
                <Select
                  value={tipoMensagem}
                  onChange={(e) => setTipoMensagem(e.target.value)}
                  label="Etapa / Mensagem"
                >
                  <MenuItem value="todos">Todas as Etapas</MenuItem>
                  <MenuItem value="1a_boas_vindas">1ª - Boas-vindas</MenuItem>
                  <MenuItem value="2a_cobranca">2ª - Cobrança</MenuItem>
                  <MenuItem value="3a_agradecimento">3ª - Agradecimento</MenuItem>
                  <MenuItem value="4a_icarus_urgente">4ª - Ícarus Urgente</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Linha 2: Período por datas e Ações */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="date"
                label="Data Início"
                InputLabelProps={{ shrink: true }}
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="date"
                label="Data Fim"
                InputLabelProps={{ shrink: true }}
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6} style={{ display: "flex", gap: 12, justifyContent: "flex-end", alignItems: "center" }}>
              <Button
                variant="outlined"
                startIcon={<RotateCcw size={16} />}
                onClick={handleLimparFiltros}
              >
                Limpar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<Search size={16} />}
                style={{ padding: "6px 20px" }}
              >
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Alerta informativo em caso de falha de conexão */}
      {errorMessage && (
        <Box
          style={{
            backgroundColor: "rgba(220, 38, 38, 0.1)",
            border: "1px solid rgba(220, 38, 38, 0.3)",
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <XCircle size={22} color="#dc2626" />
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#dc2626" }}>
                Aviso do Servidor de Automação:
              </Typography>
              <Typography variant="body2" style={{ color: "#b91c1c" }}>
                {errorMessage}
              </Typography>
            </Box>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => carregarDisparos(true)}
            style={{ color: "#dc2626", borderColor: "#dc2626" }}
          >
            Tentar Novamente
          </Button>
        </Box>
      )}

      {/* 4. Tabela de Disparos */}
      <Paper className={classes.tablePaper}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell}>Data / Hora</TableCell>
                <TableCell className={classes.tableHeaderCell}>Colaborador</TableCell>
                <TableCell className={classes.tableHeaderCell}>Etapa / Tipo</TableCell>
                <TableCell className={classes.tableHeaderCell}>Canal</TableCell>
                <TableCell className={classes.tableHeaderCell}>Destinatário</TableCell>
                <TableCell className={classes.tableHeaderCell}>Status</TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && disparos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: 40 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                      Carregando logs de disparos da automação...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : disparos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: 40 }}>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                      Nenhum disparo encontrado para os filtros selecionados.
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Tente ampliar o período ou alterar os critérios de busca.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                disparos.map((item) => (
                  <TableRow key={item.id} className={classes.tableRow}>
                    <TableCell style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                      {formatarDataHora(item.data_hora_formatada || item.created_at)}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 700 }}>
                        {item.nome_funcionario || "—"}
                      </Typography>
                      <Typography variant="caption" style={{ color: "#64748b" }}>
                        CPF: {item.cpf_formatado || item.cpf || "—"} | Matrícula: {item.matricula_ginfor || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={item.tipo_mensagem_label || item.tipo_mensagem}
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          backgroundColor: "rgba(100, 116, 139, 0.12)"
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Box className={classes.channelChip}>
                        {item.canal === "email" ? (
                          <>
                            <Mail size={15} color="#0284c7" />
                            <span>E-mail</span>
                          </>
                        ) : (
                          <>
                            <Send size={15} color="#16a34a" />
                            <span>WhatsApp</span>
                          </>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell style={{ fontSize: "0.85rem" }}>
                      {item.destinatario || item.celular || "—"}
                    </TableCell>

                    <TableCell>
                      {renderStatusBadge(item.status_envio, item.status_envio_label)}
                    </TableCell>

                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <Tooltip title="Ver Detalhes do Disparo">
                          <IconButton size="small" onClick={() => handleOpenDetails(item)}>
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>

                        {item.ticket_id && (
                          <Tooltip title={`Ver Conversa no CRM (Ticket #${item.ticket_id})`}>
                            <IconButton
                              size="small"
                              onClick={() => history.push(`/tickets/${item.ticket_id}`)}
                              style={{ color: "#2563eb" }}
                            >
                              <MessageSquare size={18} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {item.link_assinatura && (
                          <Tooltip title="Abrir Assinatura GiSign">
                            <IconButton
                              size="small"
                              href={item.link_assinatura}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#16a34a" }}
                            >
                              <ExternalLink size={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={paginacao.total_registros || 0}
          rowsPerPage={paginacao.limite || 25}
          page={(paginacao.pagina_atual || 1) - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Modal de Detalhes */}
      <DisparoDetalhesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        disparo={selectedDisparo}
      />
    </Box>
  );
};

export default RelatorioDisparosRobo;
